import React from 'react'
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Typography,
  Box,
  Chip,
  useTheme,
} from '@mui/material'

const DesktopCompositeFilterDropdown = ({ groupedFilter, onChange, selectedFilters }) => {
  const theme = useTheme()

  // Filter the grouped filters based on selectedFilters
  const selectedGroupedFilters = groupedFilter.filters.filter(
    filter => filter.filterDefinition.name && selectedFilters[filter.filterDefinition.name],
  )

  // Handler to remove a selected filter
  const handleRemoveFilter = (e, filter) => {
    e.stopPropagation()
    onChange({
      filterDefinition: filter.filterDefinition,
      filterGroupName: groupedFilter.displayName,
      selectedValues: [],
    })
  }

  return (
    <FormControl
      sx={{
        m: 1,
        width: 200,
        '& .MuiInputLabel-root': {
          fontSize: '.7rem',
          color: theme.palette.primary.dark,
          '&.MuiInputLabel-shrink': {
            color: theme.palette.primary.dark, // Dark color when shrunk
          },
        },
      }}
      size='small'
    >
      <InputLabel
        id={`label-${groupedFilter.filterGroup}`}
        sx={{
          fontSize: '.7rem',
          color: theme.palette.primary.dark,
        }}
      >
        {groupedFilter.displayName}
      </InputLabel>
      <Select
        labelId={`label-${groupedFilter.filterGroup}`}
        value={selectedGroupedFilters.map(filter => filter.filterDefinition.name)}
        multiple
        sx={{
          height: '32px', // Set a default minimum height
          display: 'flex',
          alignItems: 'flex-start', // Allow content to grow vertically
          flexWrap: 'wrap', // Wrap chips when there are multiple
          gap: 0.5,
          minHeight: '32px', // Base height
          padding: 0,
          '& .MuiSelect-select': {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            padding: '4px 8px',
            minHeight: '32px', // Ensure default compact height
            maxHeight: '72px', // Limit content height
            overflowY: 'auto',
          },
        }}
        renderValue={() => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: '72px', // Limit height for multiple chips
              overflow: 'hidden',
            }}
          >
            {selectedGroupedFilters.map(filter => (
              <Chip
                key={filter.filterDefinition.name}
                label={filter.filterDefinition.displayName}
                onDelete={e => handleRemoveFilter(e, filter)}
                onMouseDown={e => e.stopPropagation()} // Prevent dropdown from opening
                sx={{
                  fontSize: '.7rem',
                  color: theme.palette.primary.dark,
                  backgroundColor: theme.palette.grey[200],
                  height: '24px',
                  maxWidth: '150px', // Prevent chips from getting too wide
                  '& .MuiChip-label': {
                    fontSize: '.7rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100px', // Limit label width
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: '16px', // Make delete icon more visible
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300, // Limit dropdown height
              overflow: 'auto', // Allow scrolling in dropdown
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        displayEmpty
      >
        <Box sx={{ px: 2, py: 1 }}>
          {groupedFilter.filters.map(filter => {
            const isChecked = (
              selectedFilters[filter.filterDefinition.name]?.selectedValues || []
            ).some(f => f.value === '1')

            return (
              <MenuItem
                key={filter.filterDefinition.name}
                value={filter.filterDefinition.name}
                onClick={() => {
                  const selectedValues = isChecked ? [] : [{ value: '1', name: 'Yes' }]
                  onChange({
                    filterDefinition: filter.filterDefinition,
                    filterGroupName: groupedFilter.displayName,
                    selectedValues,
                  })
                }}
              >
                <Checkbox checked={isChecked} />
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: '.8rem',
                        color: theme.palette.primary.dark,
                      }}
                    >
                      {filter.filterDefinition.displayName}
                    </Typography>
                  }
                />
              </MenuItem>
            )
          })}
        </Box>
      </Select>
    </FormControl>
  )
}

export default DesktopCompositeFilterDropdown
